import React, { useState } from "react";
import { Button, Pagination } from "react-bootstrap";
import MoleculeImage from "./Smilestoimage";
import { CompoundDataStruct } from "./sample-data";
import TrafficLight from "./TrafficLight";

interface CompoundDisplayProps {
  compoundData: CompoundDataStruct;
  setPatentModal: (patentModal: any) => void;
  handleShow: () => void;
}

const CompoundDisplay: React.FC<CompoundDisplayProps> = ({
  compoundData,
  setPatentModal,
  handleShow,
}) => {
  const itemsPerPage = 3;
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPatentPage, setCurrentPatentPage] = useState(1);

  if (!compoundData || !compoundData.pubchemResults) {
    return (
      <div className="text-center mt-4 text-danger">
        No compound data available.
      </div>
    );
  }

  const { currentCompound, similarCompound, patents } =
    compoundData.pubchemResults;

  const totalPages = Math.ceil(similarCompound.length / itemsPerPage);
  const totalPatentPages = Math.ceil(patents.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCompounds = similarCompound
    .sort((a, b) => a.similarity_score - b.similarity_score)
    .slice(indexOfFirstItem, indexOfLastItem);
  const indexOfLastPatentItem = currentPatentPage * itemsPerPage;
  const indexOfFirstPatentItem = indexOfLastPatentItem - itemsPerPage;
  const currentPatents = patents.slice(
    indexOfFirstPatentItem,
    indexOfLastPatentItem
  );

  const getColor = (score: number) => {
    if (score <= 0.5) {
      return "red";
    } else if (score > 0.5 && score <= 1) {
      return "yellow";
    } else {
      return "green";
    }
  };

  const handleModal = (patent: any) => {
    setPatentModal(patent);
    handleShow();
  };

  const config = {
    red: {
      backgroundColor: "red",
    },
    yellow: {
      backgroundColor: "yellow",
    },
    green: {
      backgroundColor: "#00d768",
    },
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Compound Details</h2>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-2">
            <div>
              <div className="compound-image">
                <MoleculeImage smiles={currentCompound.smile} />
              </div>
            </div>
            <div className="compound-details">
              <div className="detail-item">
                <h3>Name</h3>
                <p className="detail-value">{currentCompound.recordTitle}</p>
              </div>
              <div className="detail-item">
                <h3>CID</h3>
                <p className="detail-value">{currentCompound.cid}</p>
              </div>
              <div className="detail-item">
                <h3>SMILES</h3>
                <div className="detail-code">{currentCompound.smile}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Similar Compounds</h2>
        </div>
        <div className="card-content">
          <div className="similar-compounds-grid">
            {currentCompounds.map((compound, index) => (
              <div key={index} className="compound-card">
                <div className="compound-card-image">
                  <MoleculeImage smiles={compound.smile} />
                </div>
                <div className="compound-card-content">
                  <div className="compound-card-header">
                    <h3
                      className="compound-card-title"
                      style={{ paddingTop: "16px", overflow: "auto" }}
                    >
                      {compound.recordTitle}
                    </h3>
                    {/* <span className="similarity-badge">87% match</span> */}
                    <span className="similarity-badge">
                      <TrafficLight
                        initialColor={getColor(compound.similarity_score)}
                        layout="horizontal"
                        config={config}
                      />
                    </span>
                  </div>
                  <p className="compound-card-subtitle">{compound.iupacName}</p>
                  <div className="compound-card-cid">
                    <p className="compound-card-smiles-label">CID:</p>
                    <p className="compound-card-subtitle">{compound.cid}</p>
                  </div>
                  <div className="compound-card-smiles">
                    <p className="compound-card-smiles-label">SMILES:</p>
                    <div className="detail-code">{compound.smile}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {similarCompound.length > 0 && (
          <Pagination className="justify-content-center mt-3">
            <Pagination.Item style={{ paddingRight: "2%" }} disabled>
              Page {currentPage} of {totalPages} | Total:{" "}
              {similarCompound.length}
            </Pagination.Item>
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, pageIndex) => (
              <Pagination.Item
                key={pageIndex}
                active={pageIndex + 1 === currentPage}
                onClick={() => setCurrentPage(pageIndex + 1)}
              >
                {pageIndex + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            />
          </Pagination>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Patents</h2>
        </div>
        <div className="card-content">
          <div className="similar-compounds-grid">
            {currentPatents.map((patent) => (
              <div className="compound-card">
                <div className="compound-card-content">
                  <div className="compound-card-header">
                    <h3 className="compound-card-title">{patent.patent_id}</h3>
                    {/* <span className="similarity-badge">87% match</span> */}
                  </div>
                  <div className="compound-card-cid">
                    <p className="compound-card-smiles-label">Kind Code:</p>
                    <p className="compound-card-subtitle">{patent.kind_code}</p>
                  </div>
                  <div className="compound-card-cid">
                    <p className="compound-card-smiles-label">Country Code:</p>
                    <p className="compound-card-subtitle">
                      {patent.country_code}
                    </p>
                  </div>
                  <div className="compound-card-cid">
                    <p className="compound-card-smiles-label">Source:</p>
                    <p className="compound-card-subtitle">{patent.source}</p>
                  </div>
                  <div className="compound-card-cid">
                    <Button
                      onClick={() => {
                        handleModal(patent);
                      }}
                      style={{
                        backgroundColor: "#4b6cb7",
                        borderColor: "#4b6cb7",
                        color: "white",
                        padding: "8px 16px",
                        borderRadius: "4px",
                        transition: "background-color 0.2s",
                      }}
                      className="patent-link"
                    >
                      View Patent
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        className="patent-link-icon"
                      >
                        <path d="M15 3h6v6"></path>
                        <path d="M10 14 21 3"></path>
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      </svg>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {patents.length <= 0 && <div>No Patents</div>}
        </div>
        {patents.length > 0 && (
          <Pagination className="justify-content-center mt-3">
            <Pagination.Item style={{ paddingRight: "2%" }} disabled>
              Page {currentPatentPage} of {totalPatentPages} | Total:{" "}
              {patents.length}
            </Pagination.Item>
            <Pagination.Prev
              onClick={() =>
                setCurrentPatentPage((prev) => Math.max(prev - 1, 1))
              }
              disabled={currentPatentPage === 1}
            />
            {[...Array(totalPatentPages)].map((_, pageIndex) => (
              <Pagination.Item
                key={pageIndex}
                active={pageIndex + 1 === currentPatentPage}
                onClick={() => setCurrentPatentPage(pageIndex + 1)}
              >
                {pageIndex + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() =>
                setCurrentPatentPage((prev) =>
                  Math.min(prev + 1, totalPatentPages)
                )
              }
              disabled={currentPatentPage === totalPatentPages}
            />
          </Pagination>
        )}
      </div>
    </>
  );
};

export default CompoundDisplay;
